/* ============================================================================
   Furniva storefront behaviour.

   Scope: the chrome (mega menu, menu drawer, search overlay) and the shared
   widgets the design specifies (hero carousel, media rails, pincode check,
   sticky buy bar, reel viewer).

   Cart mutations are deliberately NOT reimplemented here — Horizon's own cart
   components own that, and duplicating it would give two sources of truth for
   line quantities. This file only opens the drawer and reflects the count.

   Conventions:
     - every behaviour is opt-in via a data- attribute, so a section that does
       not need it pays nothing;
     - nothing here is required for navigation: menus, search and filters all
       work as plain links and forms with JavaScript off;
     - motion respects prefers-reduced-motion.
   ============================================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE_QUERY = '(max-width: 759.98px)';

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }
  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }
  function hapticSuccess() {
    if ('vibrate' in navigator) navigator.vibrate(18);
  }

  /* --------------------------------------------------------------------------
     Drag and trackpad, for anything that scrolls sideways.

     A touchscreen gets this from the browser. A mouse gets nothing: a rail full
     of product cards sits there looking draggable and does not move, and the
     only way along it is a pair of arrows in the corner. A trackpad is worse —
     a two-finger sideways flick is a wheel event with deltaX, which a native
     scroller answers, but a carousel driven any other way ignores entirely.

     A row that really is a scroller needs nothing: the browser already answers a
     sideways trackpad flick, and answers it better than any handler would.
     `stepGestures` is for the carousels that are NOT scrollers — a transform
     track, a stack of slides — where the browser has nothing to work with; it
     reads horizontal movement as one step at a time and never touches a
     vertical wheel.

     A gesture must never open the thing underneath it, so past a few pixels of
     travel the click that ends one is swallowed in the capture phase.
     -------------------------------------------------------------------------- */
  var DRAG_SLOP = 4;

  /* There is deliberately no wheel handler for the rows that are real
     scrollers.

     One was written and taken straight back out. It answered a VERTICAL wheel
     over a rail by scrolling the rail sideways, which meant that with the
     pointer anywhere over a row of cards the page could not be scrolled at all
     — the section trapped you. A horizontal trackpad flick over a horizontal
     scroller is something the browser already does natively and does better,
     so the right amount of code here is none.

     `stepGestures` below is for the carousels that are NOT scrollers, where the
     browser has nothing to work with; it acts only on horizontal movement and
     never touches a vertical wheel.
     ---------------------------------------------------------------------- */

  /* Turn a horizontal wheel or drag into discrete steps, for the carousels that
     are not scrollers at all — a transform track, a stack of slides. `step` is
     called with 1 or -1 and is expected to move by one. */
  function stepGestures(el, step) {
    if (!el || el.dataset.fvStepBound) return;
    el.dataset.fvStepBound = '1';

    var wheelRun = 0;
    var wheelLock = false;
    on(el, 'wheel', function (event) {
      if (event.ctrlKey) return;
      var across = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
      if (!across) return;
      event.preventDefault();
      if (wheelLock) return;
      wheelRun += across;
      // A trackpad fires a long tail of small deltas for one flick; the
      // threshold plus the lock turn that into exactly one step.
      if (Math.abs(wheelRun) < 45) return;
      step(wheelRun > 0 ? 1 : -1);
      wheelRun = 0;
      wheelLock = true;
      window.setTimeout(function () { wheelLock = false; }, 420);
    }, { passive: false });

    var pointerId = null;
    var startX = 0;
    var fired = false;
    var swallow = false;

    el.addEventListener('click', function (event) {
      if (!swallow) return;
      swallow = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    on(el, 'pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      fired = false;
      swallow = false;
      el.classList.add('is-dragging');
    });
    on(el, 'pointermove', function (event) {
      if (pointerId !== event.pointerId || fired) return;
      var delta = event.clientX - startX;
      if (Math.abs(delta) > DRAG_SLOP) swallow = true;
      // One step per drag: a carousel that flew several slides on a long drag
      // would be impossible to land on purpose.
      if (Math.abs(delta) < 60) return;
      fired = true;
      step(delta < 0 ? 1 : -1);
    });
    function release(event) {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      el.classList.remove('is-dragging');
    }
    on(el, 'pointerup', release);
    on(el, 'pointercancel', release);
  }

  /* --------------------------------------------------------------------------
     Horizontal swipe.

     Gestures used to be gated on `max-width`, which is a guess about the input
     device dressed up as a layout question: an iPad in landscape and a touch
     laptop are both wide, and both were left with carousels that answered only
     their arrows. Touch events answer the real question by simply not firing
     on a machine with no touchscreen, so no breakpoint is consulted here and
     a hybrid laptop gets the gesture alongside its trackpad.

     The listeners are passive and never call preventDefault, so a vertical
     drag keeps its native scroll and its momentum. A gesture counts as a swipe
     only when the horizontal travel clears MIN_SWIPE and clearly beats the
     vertical, so a diagonal flick while reading the page is not mistaken for
     one.

     A finished swipe still ends in a click on whatever was under the finger —
     on a reel frame that is a full-bleed "open full screen" button — so the
     tap that follows a recognised swipe is swallowed in the capture phase,
     before it reaches anything that would act on it.

     `handler` receives 1 for a leftward swipe (advance) and -1 for a rightward
     one, and may return false to decline the gesture; declining leaves the
     following tap alone.
     -------------------------------------------------------------------------- */
  var MIN_SWIPE = 44;

  function onSwipe(el, handler, ignoreSelector) {
    if (!el) return;
    var start = null;
    var swallow = false;
    var swallowTimer = null;

    el.addEventListener('click', function (event) {
      if (!swallow) return;
      swallow = false;
      window.clearTimeout(swallowTimer);
      event.preventDefault();
      event.stopPropagation();
    }, true);

    on(el, 'touchstart', function (event) {
      if (event.touches.length !== 1 || (ignoreSelector && event.target.closest(ignoreSelector))) {
        start = null;
        return;
      }
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true });

    on(el, 'touchend', function (event) {
      if (!start || event.changedTouches.length !== 1) { start = null; return; }
      var dx = event.changedTouches[0].clientX - start.x;
      var dy = event.changedTouches[0].clientY - start.y;
      start = null;
      if (Math.abs(dx) < MIN_SWIPE || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      if (handler(dx < 0 ? 1 : -1) === false) return;
      swallow = true;
      window.clearTimeout(swallowTimer);
      // A swipe that happens to end outside any clickable thing leaves the
      // flag set; it is cleared on a timer so the next honest tap is not eaten.
      swallowTimer = window.setTimeout(function () { swallow = false; }, 500);
    }, { passive: true });

    on(el, 'touchcancel', function () { start = null; }, { passive: true });
  }

  /* --------------------------------------------------------------------------
     Focus management for overlays.
     An overlay traps Tab, restores focus to whatever opened it, and closes on
     Escape — the three things a dialog has to get right.
     -------------------------------------------------------------------------- */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, event) {
    var nodes = $$(FOCUSABLE, container).filter(function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function Overlay(panel, options) {
    options = options || {};
    this.panel = panel;
    this.scrim = options.scrim || null;
    this.onOpen = options.onOpen || null;
    this.onClose = options.onClose || null;
    this.closeDuration = options.closeDuration || 280;
    // A panel that places focus itself (search) opts out of the generic focus.
    this.manageFocus = options.manageFocus !== false;
    this.returnTo = null;
    this.isOpen = false;
    this.keydown = this.handleKeydown.bind(this);
  }

  Overlay.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') trapFocus(this.panel, event);
  };

  Overlay.prototype.open = function (trigger) {
    if (this.isOpen || !this.panel) return;
    this.isOpen = true;
    this.returnTo = trigger || document.activeElement;
    this.panel.hidden = false;
    if (this.scrim) this.scrim.hidden = false;
    // Read layout once so the closed position is committed before .is-open
    // lands. A requestAnimationFrame alone runs before that frame's style
    // pass, so unhiding and opening were computed together and the filters
    // drawer and the sort sheet snapped open with no movement at all.
    void this.panel.offsetWidth;
    this.panel.classList.add('is-open');
    if (this.scrim) this.scrim.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.keydown);
    if (this.onOpen) this.onOpen();

    if (this.manageFocus) {
      var target = this.panel.querySelector('[data-fv-autofocus]') || this.panel.querySelector(FOCUSABLE);
      if (target) target.focus({ preventScroll: true });
    }
    if (this.returnTo && this.returnTo.setAttribute) this.returnTo.setAttribute('aria-expanded', 'true');
  };

  Overlay.prototype.close = function () {
    if (!this.isOpen || !this.panel) return;
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    if (this.scrim) this.scrim.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.keydown);

    var finish = function () {
      if (this.isOpen) return;
      this.panel.hidden = true;
      if (this.scrim) this.scrim.hidden = true;
    }.bind(this);

    if (reduceMotion.matches) finish();
    else window.setTimeout(finish, this.closeDuration);

    if (this.onClose) this.onClose();
    if (this.returnTo && this.returnTo.focus) {
      this.returnTo.focus({ preventScroll: true });
      if (this.returnTo.setAttribute) this.returnTo.setAttribute('aria-expanded', 'false');
    }
  };

  /* --------------------------------------------------------------------------
     Mega menu.
     Opens on click and on hover (desktop pointers only — a hover-open menu on a
     touch device eats the first tap). Closes on pointer-leave, Escape, and on
     any click outside the header.
     -------------------------------------------------------------------------- */
  function initMega() {
    var header = $('[data-fv-header]');
    var triggers = $$('[data-fv-mega-toggle]');
    var panels = $$('[data-fv-mega-panel]');
    if (!header || !triggers.length || !panels.length) return;

    var closeTimer = null;
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    var activeTrigger = null;
    var activePanel = null;

    function close() {
      var panel = activePanel;
      var trigger = activeTrigger;
      if (panel) {
        panel.classList.remove('is-open');
        window.clearTimeout(panel.__fvCloseTimer);
        if (reduceMotion.matches) panel.hidden = true;
        else panel.__fvCloseTimer = window.setTimeout(function () { panel.hidden = true; }, 60);
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      activeTrigger = null;
      activePanel = null;
    }

    function open(trigger) {
      window.clearTimeout(closeTimer);
      var panel = document.getElementById(trigger.getAttribute('data-fv-mega-toggle'));
      if (!panel) return;
      if (activePanel && activePanel !== panel) close();
      window.clearTimeout(panel.__fvCloseTimer);
      var wasHidden = panel.hidden;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      activeTrigger = trigger;
      activePanel = panel;
      if (wasHidden) void panel.offsetWidth; // commit the closed state, as in Overlay.open
      window.requestAnimationFrame(function () {
        if (activePanel === panel) panel.classList.add('is-open');
      });
    }
    function closeSoon() {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(close, 30);
    }

    triggers.forEach(function (trigger) {
      on(trigger, 'click', function (event) {
        /* A heading that is a link goes to its page. With a mouse the menu
           has already opened on hover; on a touch screen the first tap opens
           the menu and a second tap on the same heading goes to the page. */
        var href = trigger.getAttribute('href');
        if (href && (canHover.matches || activeTrigger === trigger)) {
          close();
          return;
        }
        event.preventDefault();
        if (activeTrigger === trigger) close();
        else open(trigger);
      });
      // From the keyboard, Enter follows the link and Down arrow opens the menu.
      on(trigger, 'keydown', function (event) {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        open(trigger);
        var first = activePanel && activePanel.querySelector('a[href]');
        if (first) first.focus();
      });

      if (canHover.matches) {
        on(trigger, 'mouseenter', function () { open(trigger); });
      }
    });

    panels.forEach(function (panel) {
      on(panel, 'mouseenter', function () { window.clearTimeout(closeTimer); });
      on(panel, 'mouseleave', closeSoon);
    });
    on(header, 'mouseleave', closeSoon);
    $$('[data-fv-mega-leave]').forEach(function (link) {
      on(link, 'mouseenter', closeSoon);
    });
    /* Moving onto the logo, search, wishlist, account or cart means the
       shopper has left the menu, even though the pointer is still inside the
       header (owner, 2026-10-12). */
    $$('.fv-header__logo, .fv-header__actions', header).forEach(function (zone) {
      on(zone, 'mouseenter', closeSoon);
    });

    on(document, 'keydown', function (event) {
      if (event.key === 'Escape' && activePanel) {
        var returnTo = activeTrigger;
        close();
        if (returnTo) returnTo.focus();
      }
    });

    on(document, 'click', function (event) {
      if (!activePanel) return;
      if (!header.contains(event.target)) close();
    });
  }

  /* --------------------------------------------------------------------------
     Menu drawer (below 760px).
     -------------------------------------------------------------------------- */
  function initNavDrawer() {
    var drawer = $('[data-fv-nav-drawer]');
    var scrim = $('[data-fv-nav-scrim]');
    if (!drawer) return;

    var overlay = new Overlay(drawer, { scrim: scrim, closeDuration: 280 });

    $$('[data-fv-nav-open]').forEach(function (button) {
      on(button, 'click', function () {
        overlay.open(button);
      });
    });
    $$('[data-fv-nav-close]').forEach(function (button) {
      on(button, 'click', function () {
        overlay.close();
      });
    });
    on(scrim, 'click', function () {
      overlay.close();
    });

    // Leaving the mobile breakpoint with the drawer open would strand it over
    // a desktop layout that already shows the same links inline.
    var mq = window.matchMedia(MOBILE_QUERY);
    var onChange = function (event) {
      if (!event.matches && overlay.isOpen) overlay.close();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* --------------------------------------------------------------------------
     Search overlay + predictive search.

     The form is the contract: Enter always reaches /search. Suggestions are an
     enhancement, and every failure path leaves the form usable.
     -------------------------------------------------------------------------- */
  function initSearch() {
    var root = $('[data-fv-search]');
    if (!root) return;
    if (root.dataset.fvSearchBound) return;
    root.dataset.fvSearchBound = '1';

    var panel = $('[data-fv-search-panel]', root);
    var form = $('[data-fv-search-form]', root);
    var input = $('[data-fv-search-input]', root);
    var results = $('[data-fv-search-results]', root);
    var inner = $('[data-fv-search-inner]', root);
    var clear = $('[data-fv-search-clear]', root);
    var viewAllWrap = $('[data-fv-search-viewall-wrap]', root);
    var echo = $('[data-fv-search-echo]', root);
    var resting = inner ? inner.innerHTML : '';

    /* iPhone (owner, 2026-10-10: the panel snapped open there). Focusing the
       field while the panel slides made Safari raise the keyboard and jump the
       page mid-slide, and the generic overlay focus then moved focus to the
       back arrow (keyboard down again, a ring round the arrow). Now a hidden
       field at the top takes focus inside the tap — the only moment iOS will
       raise the keyboard — the panel slides undisturbed, and focus moves to
       the real field when it lands; the keyboard stays up throughout. */
    var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    var focusTimer = null;
    function focusField() {
      if (!input) return;
      if (!isIOS || reduceMotion.matches) {
        input.focus({ preventScroll: true });
        return;
      }
      var proxy = document.createElement('input');
      proxy.type = 'text';
      proxy.setAttribute('aria-hidden', 'true');
      proxy.tabIndex = -1;
      proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;pointer-events:none;';
      document.body.appendChild(proxy);
      proxy.focus({ preventScroll: true });
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(function () {
        if (root.classList.contains('is-open')) input.focus({ preventScroll: true });
        proxy.remove();
      }, 420);
    }

    var overlay = new Overlay(root, {
      closeDuration: 280,
      manageFocus: false,
      onOpen: function () {
        if (input) {
          input.value = '';
          focusField();
        }
        render(resting);
        if (clear) clear.hidden = true;
        if (viewAllWrap) viewAllWrap.hidden = true;
      }
    });

    on(document, 'click', function (event) {
      var button = event.target.closest('[data-fv-search-open]');
      if (!button) return;
      event.preventDefault();
      overlay.open(button);
      // A fresh line every time it opens. Picking once at page load meant the
      // same sentence for as long as the tab stayed open, whatever the point of
      // writing two dozen of them was — and it has to happen AFTER the panel
      // opens, because opening it re-renders the resting state from the server
      // and puts the default line back.
      initSearchGreeting();
    });
    $$('[data-fv-search-close]').forEach(function (button) {
      on(button, 'click', function () {
        overlay.close();
      });
    });
    // Clicking the backdrop, but not the panel, dismisses.
    on(root, 'click', function (event) {
      if (panel && !panel.contains(event.target)) overlay.close();
    });

    function render(html) {
      if (inner) inner.innerHTML = html;
      // New tiles: let the shared binders give them their photo fades,
      // galleries and hearts, as on every other surface (2026-10-10).
      document.dispatchEvent(new CustomEvent('furniva:collection:updated'));
    }

    function renderTemplate(name, query, chips) {
      var tpl = $('[data-fv-search-tpl-' + name + ']');
      if (!tpl || !inner) return;
      var node = tpl.content.cloneNode(true);
      $$('[data-slot="q"]', node).forEach(function (slot) {
        slot.textContent = query;
      });
      var chipSlot = $('[data-slot="chips"]', node);
      if (chipSlot && chips) chipSlot.innerHTML = chips;
      inner.innerHTML = '';
      inner.appendChild(node);
    }

    var controller = null;
    var debounce = null;
    var lastQuery = '';

    function request(query) {
      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
      url +=
        'search/suggest?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=6' +
        '&section_id=furniva-predictive-search';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Suggest request failed: ' + response.status);
          return response.text();
        })
        .then(function (text) {
          // Only paint if this is still the query the customer is looking at.
          if (query !== lastQuery) return;
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var payload = parsed.querySelector('[data-fv-search-payload]');
          if (!payload) {
            renderTemplate('empty', query, popularChips());
            return;
          }
          if (payload.dataset.empty === 'true') {
            renderTemplate('empty', query, popularChips());
          } else {
            render(payload.innerHTML);
          }
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          if (query !== lastQuery) return;
          renderTemplate('failed', query);
          var retry = $('[data-fv-search-retry]', inner);
          on(retry, 'click', function () {
            runQuery(query, true);
          });
        });
    }

    function popularChips() {
      // Reuse the chips already rendered in the resting state rather than
      // keeping a second copy of the term list in JS.
      var holder = document.createElement('div');
      holder.innerHTML = resting;
      var chips = $$('.fv-search__chip', holder);
      return chips
        .map(function (chip) {
          return chip.outerHTML;
        })
        .join('');
    }

    function runQuery(query, immediate) {
      lastQuery = query;
      if (echo) echo.textContent = query;
      if (clear) clear.hidden = query.length === 0;

      if (query.length < 2) {
        if (viewAllWrap) viewAllWrap.hidden = true;
        render(resting);
        return;
      }

      if (viewAllWrap) viewAllWrap.hidden = false;
      renderTemplate('loading', query);

      window.clearTimeout(debounce);
      if (immediate) request(query);
      else
        debounce = window.setTimeout(function () {
          request(query);
        }, 220);
    }

    on(input, 'input', function () {
      runQuery(input.value.trim());
    });

    on(clear, 'click', function () {
      input.value = '';
      input.focus();
      runQuery('');
    });

    // Popular terms are real links to the full results page. Do not intercept
    // them with a predictive-search request that may fail or appear to do nothing.

    // Arrow keys walk the suggestion list; Enter on a highlighted row follows it.
    on(input, 'keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      var rows = $$('.fv-search__row', results);
      if (!rows.length) return;
      event.preventDefault();
      var index = rows.findIndex(function (row) {
        return row.getAttribute('aria-selected') === 'true';
      });
      rows.forEach(function (row) {
        row.setAttribute('aria-selected', 'false');
      });
      var next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      if (next < 0) next = rows.length - 1;
      if (next >= rows.length) next = 0;
      rows[next].setAttribute('aria-selected', 'true');
      rows[next].scrollIntoView({ block: 'nearest' });
    });

    on(form, 'submit', function (event) {
      var selected = $('.fv-search__row[aria-selected="true"]', results);
      if (selected && selected.href) {
        event.preventDefault();
        window.location.href = selected.href;
        return;
      }
      if (!input.value.trim()) event.preventDefault();
    });
  }

  /* --------------------------------------------------------------------------
     Cart drawer hand-off.
     Horizon owns the cart drawer; the Furniva header just opens it. If the
     drawer component is not on the page, the link falls through to /cart.
     -------------------------------------------------------------------------- */
  function initCart() {
    $$('[data-fv-cart-open]').forEach(function (link) {
      on(link, 'click', function (event) {
        // The Furniva overlay owns this interaction when present. Calling
        // Horizon's drawer as well caused two competing open animations.
        if (window.furnivaCartDrawer) return;
        // No drawer on a phone — see the note in furniva-cart-drawer.js. This
        // is Horizon's fallback path and has to agree with it, or the drawer
        // would come back wherever the Furniva one had not loaded.
        if (window.matchMedia && window.matchMedia('(max-width: 759.98px)').matches) return;
        var drawer = document.querySelector('theme-drawer#cart-drawer');
        if (!drawer || typeof drawer.open !== 'function') return; // let the link navigate
        event.preventDefault();
        drawer.open();
      });
    });

    // Horizon's cart updates reach the header count through furniva-addfx.js,
    // which owns every badge on the page and animates the change. Setting the
    // text here as well would race it and skip the animation.
  }

  /* --------------------------------------------------------------------------
     The line of welcome above search.

     Which line is chosen depends on who is asking and what time it is where
     they are. The hour has to be read from the browser: Shopify renders Liquid
     in the shop's timezone, so a greeting written server-side would tell a
     customer in London good evening over their breakfast. The lines themselves
     live in furniva-search-greeting.liquid, which is where they should be
     edited; this only picks one.

     If anything here is missing or malformed the paragraph keeps the line
     Liquid already printed into it, so the panel is never headed by a blank.
     -------------------------------------------------------------------------- */
  /* The resting panel is re-rendered from the server whenever search returns to
     it — opening it, clearing the field — and each render brings the default
     line back with it. Watching the panel's own children means the greeting is
     re-picked every time it reappears, however it got there.

     Only direct children are watched, and deliberately: writing the new line
     sets textContent on a descendant, which would re-trigger a subtree
     observer and loop. */
  function watchSearchGreeting() {
    var inner = $('[data-fv-search-inner]');
    if (!inner || inner.dataset.fvGreetWatch || typeof MutationObserver !== 'function') return;
    inner.dataset.fvGreetWatch = '1';
    new MutationObserver(function () { initSearchGreeting(); }).observe(inner, { childList: true });
  }

  /**
   * The first name of whoever is signed in, or ''.
   *
   * The greeting's `data-fv-greet-who` and `data-fv-greet-name` come from
   * Liquid's `customer.first_name`, and on this store that is ALWAYS blank:
   * sign-in is by phone through the account Worker, so Shopify holds no
   * session and `customer` is nil on every request. The greeting could
   * therefore never say anybody's name, and a name saved in the profile never
   * reached it.
   *
   * The Worker's session name is the one the header already reads, so the
   * greeting reads the same key. It is a display name and nothing more —
   * nothing here is an authorisation decision.
   */
  function accountName() {
    try {
      return (window.localStorage.getItem('furniva:account:name') || '').trim();
    } catch (error) {
      return '';
    }
  }

  function initSearchGreeting() {
    var hour = new Date().getHours();
    var when =
      hour < 5 ? 'night' :
      hour < 12 ? 'morning' :
      hour < 17 ? 'afternoon' :
      hour < 22 ? 'evening' : 'night';
    var signedInName = accountName();

    // The overlay's greeting is on every page, and the search page renders one
    // of its own, so there can be two. Each is paired with the script element
    // the snippet prints directly after it.
    $$('[data-fv-search-greet]').forEach(function (line) {
      var source = line.nextElementSibling;
      if (!source || !source.matches('[data-fv-greet-lines]')) return;

      var sets;
      try {
        sets = JSON.parse(source.textContent);
      } catch (error) {
        return; // the server-rendered line stands
      }

      // The session the Worker holds wins over Liquid's, which cannot know.
      var name = signedInName || line.dataset.fvGreetName || '';
      var who = name ? 'known' : line.dataset.fvGreetWho === 'known' ? 'known' : 'guest';
      var options = sets[who] && sets[who][when];
      if (!options || !options.length) return;

      // Not the one already showing, so reopening search visibly changes it.
      var pool = options.filter(function (option) {
        return option.replace('{name}', name) !== line.textContent.trim();
      });
      if (!pool.length) pool = options;
      var chosen = pool[Math.floor(Math.random() * pool.length)];
      line.textContent = chosen.replace('{name}', name);
    });
  }

  function initCardQuickAdd(scope) {
    $$('.fv-card__quickadd-form', scope || document).forEach(function (form) {
      if (form.dataset.fvQuickBound) return;
      form.dataset.fvQuickBound = '1';
      on(form, 'submit', function (event) {
        event.preventDefault();
        var button = $('button[type="submit"]', form);
        if (window.FurnivaAddFx) window.FurnivaAddFx.working(button);
        var routeRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Could not add this piece.');
            return response.json();
          })
          .then(function (cart) {
            if (window.FurnivaAddFx) window.FurnivaAddFx.added(button, cart);
            else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          })
          .catch(function () {
            if (window.FurnivaAddFx) window.FurnivaAddFx.failed(button);
          });
      });
    });
  }

  /* --------------------------------------------------------------------------
     Carousel — the homepage hero and any other slide set.
     One slide visible at a time, dots + prev/next, optional autoplay that stops
     on interaction and never runs under reduced motion.
     -------------------------------------------------------------------------- */
  function initCarousels() {
    $$('[data-fv-carousel]').forEach(function (root) {
      var slides = $$('[data-fv-slide]', root);
      if (slides.length < 2) return;

      var dots = $$('[data-fv-dot]', root);
      var prev = $('[data-fv-prev]', root);
      var next = $('[data-fv-next]', root);
      var live = $('[data-fv-carousel-live]', root);
      var index = 0;
    var reelSettle = null;
    var seconds = parseFloat(root.dataset.autoplay || '0');
    var timer = null;
    var paused = false;
      var timer = null;
      var interval = parseInt(root.dataset.fvInterval, 10) || 6000;
      var autoplay = root.dataset.fvAutoplay === 'true' && !reduceMotion.matches;

      /* Two ways to change slide.

         The default swaps `hidden`, which is right for a rail of rooms or
         reels: one thing is on screen and the next replaces it.

         The hero asks for `slide` instead. An element carrying `hidden` is
         `display: none` and cannot animate out, so a stack of them can only
         ever cross-fade — which at hero size reads as a blink rather than a
         carousel. In slide mode nothing is hidden: every slide keeps its box
         and is placed to the left of, on, or to the right of the stage, and
         moving between them is a transform the browser can interpolate.

         Inactive slides stay out of the accessibility tree and out of reach of
         a pointer or the tab key, which `hidden` used to do for free. */
      var mode = root.dataset.fvMode === 'slide' ? 'slide' : 'swap';

      function show(next_index) {
        index = (next_index + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          var active = i === index;
          if (mode === 'slide') {
            slide.dataset.fvSlideState = active ? 'active' : i < index ? 'before' : 'after';
            if (active) slide.removeAttribute('inert');
            else slide.setAttribute('inert', '');
          } else {
            slide.hidden = !active;
          }
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        dots.forEach(function (dot, i) {
          var active = i === index;
          dot.setAttribute('aria-current', active ? 'true' : 'false');
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (live) live.textContent = 'Slide ' + (index + 1) + ' of ' + slides.length;
      }

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }
      function start() {
        if (!autoplay || timer) return;
        timer = window.setInterval(function () {
          show(index + 1);
        }, interval);
      }

      on(prev, 'click', function () {
        stop();
        show(index - 1);
      });
      on(next, 'click', function () {
        stop();
        show(index + 1);
      });
      dots.forEach(function (dot, i) {
        on(dot, 'click', function () {
          stop();
          show(i);
        });
      });

      // The hero is a stack of hidden/shown slides, so there is no rail for a
      // finger to push; without this the arrows were the only way through it
      // on every touchscreen, phones included.
      onSwipe(root, function (direction) {
        stop();
        show(index + direction);
      });

      // Autoplay yields to anyone actually reading or tabbing through.
      on(root, 'mouseenter', stop);
      on(root, 'focusin', stop);
      on(root, 'mouseleave', start);
      on(document, 'visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      show(0);
      start();
    });
  }

  /* --------------------------------------------------------------------------
     Sideways rows for a mouse, and a trackpad that still scrolls the page
     (owner, 2026-10-01).

     The home page has no arrows, and a mouse has no sideways gesture, so on a
     computer without a trackpad a row could not be moved at all. Every
     sideways row now follows a press-and-drag of the mouse: it glides on when
     let go, lands on a card, and a drag never opens the card under it.

     A trackpad swipe that is mostly up or down over a row scrolls the page,
     even when it starts with a little sideways drift. Without this Chrome hands
     the whole swipe to the row, which can only move sideways, and the page
     stops dead under the pointer. The first movement of a swipe decides it; a
     mouse wheel, with no sideways part, is left to the browser.

     Touch is untouched throughout: a finger on a native scroller already does
     all of this, better.
     -------------------------------------------------------------------------- */
  var OWN_GESTURES = '.fv-media__mobile-rail, [data-fv-reel-track], [data-fv-gal-track], .fv-spin360, ' +
    'input, select, textarea, [contenteditable], .fv-tablewrap, .fv-aplus__html';

  /* The sideways scroller the pointer is over, if it can actually move. */
  function sideScroller(node) {
    for (; node && node !== document.body && node.nodeType === 1; node = node.parentElement) {
      if (node.matches(OWN_GESTURES)) return null;
      if (node.scrollWidth > node.clientWidth + 4 && /auto|scroll/.test(getComputedStyle(node).overflowX)) return node;
    }
    return null;
  }

  /* Glide to `left`, settling on the nearest card when the row snaps. Snap is
     held off while the row is dragged, so it is put back only once the row has
     arrived exactly on a card and there is nothing for it to jump to. */
  function settleRow(row, left) {
    var max = row.scrollWidth - row.clientWidth;
    if (getComputedStyle(row).scrollSnapType.indexOf('x') !== -1 || row.classList.contains('is-mouse-dragging')) {
      var base = row.getBoundingClientRect().left + (parseFloat(getComputedStyle(row).scrollPaddingLeft) || 0);
      var best = null;
      Array.prototype.forEach.call(row.children, function (child) {
        var at = child.getBoundingClientRect().left - base + row.scrollLeft;
        if (best === null || Math.abs(at - left) < Math.abs(best - left)) best = at;
      });
      if (best !== null) left = best;
    }
    left = Math.max(0, Math.min(max, left));
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      row.classList.remove('is-mouse-dragging');
    };
    if (Math.abs(left - row.scrollLeft) < 1) { finish(); return; }
    row.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if ('onscrollend' in row) row.addEventListener('scrollend', finish, { once: true });
    window.setTimeout(finish, 900);
  }

  /* What scrolls the page around a row: the theme's page wrapper where that
     is the scroller, otherwise the document. */
  function pageScroller(row) {
    for (var node = row.parentElement; node && node !== document.body; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight + 4 && /auto|scroll/.test(getComputedStyle(node).overflowY)) return node;
    }
    return document.scrollingElement || document.documentElement;
  }

  function bindWheelAxis(row) {
    if (row.__fvWheelAxis) return;
    row.__fvWheelAxis = true;
    var axis = null;
    var last = 0;
    row.addEventListener('wheel', function (event) {
      if (event.ctrlKey || event.shiftKey) return;
      // A pause longer than a trackpad's own spacing begins a new swipe.
      if (event.timeStamp - last > 160) axis = null;
      last = event.timeStamp;
      if (axis === null) {
        axis = event.deltaX !== 0 && Math.abs(event.deltaY) > Math.abs(event.deltaX) ? 'page' : 'native';
      }
      if (axis !== 'page' || !event.cancelable) return;
      event.preventDefault();
      var unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      pageScroller(row).scrollBy({ top: event.deltaY * unit, left: 0, behavior: 'instant' });
    }, { passive: false });
  }

  function initRowGestures() {
    var drag = null;
    var swallow = false;

    // Rows are found as the pointer meets them, so rows added later (a
    // collection swapping its grid, a rail fetched in) need nothing extra.
    on(document, 'pointerover', function (event) {
      if (event.pointerType === 'touch') return;
      var row = sideScroller(event.target);
      if (row) bindWheelAxis(row);
    }, { passive: true });

    on(document, 'pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      var row = sideScroller(event.target);
      if (!row) return;
      drag = { row: row, id: event.pointerId, x: event.clientX, left: row.scrollLeft, moved: false, trail: [] };
    });

    on(document, 'pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) <= DRAG_SLOP) return;
        drag.moved = true;
        drag.row.classList.add('is-mouse-dragging');
        var selection = window.getSelection && window.getSelection();
        if (selection) selection.removeAllRanges();
      }
      drag.row.scrollLeft = drag.left - dx;
      drag.trail.push([event.timeStamp, event.clientX]);
      if (drag.trail.length > 6) drag.trail.shift();
    });

    function release(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var done = drag;
      drag = null;
      if (!done.moved) return;
      swallow = true;
      window.setTimeout(function () { swallow = false; }, 80);
      // Carry on in the direction of the last flick, as a touch scroller does.
      var first = done.trail[0];
      var last = done.trail[done.trail.length - 1];
      var velocity = first && last && last[0] - first[0] > 0 && event.timeStamp - last[0] < 80
        ? (last[1] - first[1]) / (last[0] - first[0])
        : 0;
      settleRow(done.row, done.row.scrollLeft - velocity * 260);
    }
    on(document, 'pointerup', release);
    on(document, 'pointercancel', release);

    // The click that ends a drag lands on whatever card was under the mouse.
    document.addEventListener('click', function (event) {
      if (!swallow) return;
      swallow = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    // Links and photographs are natively draggable, which would steal the
    // gesture from the row.
    on(document, 'dragstart', function (event) {
      if (sideScroller(event.target)) event.preventDefault();
    });
  }

  /* --------------------------------------------------------------------------
     Rails — horizontal scrollers with arrow controls.
     Arrows hide when there is nothing to scroll to, so they never sit dead.
     -------------------------------------------------------------------------- */
  function initRails(scope) {
    $$('[data-fv-rail]', scope || document).forEach(function (rail) {
      // Rails can be injected after load (the similar-products fetch replaces
      // its placeholder), so initRails is re-runnable. A rail that has already
      // been wired is skipped rather than given a second set of listeners.
      if (rail.dataset.fvRailReady) return;
      rail.dataset.fvRailReady = '1';

      var wrapper = rail.closest('[data-fv-rail-wrap]') || rail.parentElement;
      var prev = $('[data-fv-rail-prev]', wrapper);
      var next = $('[data-fv-rail-next]', wrapper);

      function step() {
        var first = rail.firstElementChild;
        return first ? first.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
      }
      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;
        var scrollable = rail.scrollWidth > rail.clientWidth + 4;
        if (prev) prev.hidden = !scrollable;
        if (next) next.hidden = !scrollable;
        // Hiding the two controls leaves the nav row in the layout with no
        // visible content, and the wrap's gap still reserves space under the
        // rail. The class lets a section collapse that gap; sections that want
        // the reserved height simply ignore it.
        if (wrapper) wrapper.classList.toggle('is-static', !scrollable);
      }

      on(prev, 'click', function () {
        rail.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      on(next, 'click', function () {
        rail.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      on(rail, 'scroll', sync, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
      sync();
    });
  }

  /* --------------------------------------------------------------------------
     Sticky buy bar — the mobile purchase bar on the product page.
     Appears once the real Add to cart has scrolled out of view.
     -------------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     How much room the bottom navigation takes.

     Anything else pinned to the bottom edge — the sticky purchase bar — rests
     on top of it rather than over it, and needs a number to rest at. The
     navigation's height is not a constant: it carries the safe-area inset for
     the home indicator, its labels shrink below 360px, and it is not rendered
     at all above the navigation breakpoint. So it is measured rather than
     assumed, and re-measured whenever it changes size or the page is rotated.

     Published on the root element as --fv-bottom-nav-h; 0px when the bar is not
     on screen, so a desktop layout reading the same property gets a sane value.
     -------------------------------------------------------------------------- */
  function initBottomNavHeight() {
    var nav = $('.fv-bottom-nav');
    var root = document.documentElement;

    function publish() {
      var height = 0;
      if (nav && getComputedStyle(nav).display !== 'none') {
        height = Math.round(nav.getBoundingClientRect().height);
      }
      root.style.setProperty('--fv-bottom-nav-h', height + 'px');
    }

    publish();
    if (!nav) return;
    if (typeof ResizeObserver === 'function') new ResizeObserver(publish).observe(nav);
    on(window, 'resize', publish);
    on(window, 'orientationchange', publish);
  }

  /* --------------------------------------------------------------------------
     Arrows for "Where do you want to start".

     The rows in this section are real scrollers, exactly like the product rails,
     but they were written without the rail's nav markup — so they had no way
     along them but a trackpad. Rather than repeat that markup in Liquid for
     every group of every panel, each grid is adapted into the rail contract
     here: wrapped, marked, given the same two buttons, and then handed to
     initRails, which already knows how to disable them at the ends and hide
     them when the row fits.

     The icons are inlined because the Liquid icon snippet cannot be rendered
     from script; they are the same two paths it would have printed.
     -------------------------------------------------------------------------- */
  function initExplorerRails() {
    var nav =
      '<div class="fv-railnav"><div class="fv-railnav__buttons">' +
      '<button type="button" class="fv-railnav__btn" data-fv-rail-prev aria-label="Scroll back" hidden>' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--fv-walnut)" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg></button>' +
      '<button type="button" class="fv-railnav__btn" data-fv-rail-next aria-label="Scroll forward" hidden>' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--fv-walnut)" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg></button>' +
      '</div></div>';

    $$('.fv-explorer__grid').forEach(function (grid) {
      if (grid.dataset.fvRailAdapted) return;
      grid.dataset.fvRailAdapted = '1';

      var wrap = document.createElement('div');
      wrap.className = 'fv-railwrap fv-explorer__railwrap';
      wrap.setAttribute('data-fv-rail-wrap', '');
      grid.parentNode.insertBefore(wrap, grid);
      wrap.appendChild(grid);
      grid.setAttribute('data-fv-rail', '');
      wrap.insertAdjacentHTML('beforeend', nav);
    });

    // initRails binds the arrows, the end states and the trackpad in one pass.
    initRails();
  }

  /* --------------------------------------------------------------------------
     Main-menu icons only when the row has room for them (owner, 2026-10-01).
     The header is capped in width, and what sits either side of the menu
     changes (a cart count, the festive button), so the fit is measured, not
     guessed: with the icons in, the headings must fit between the logo and
     the actions; if they do not, the icons go and the plain headings stay.
     -------------------------------------------------------------------------- */
  function initNavFit() {
    var nav = $('.fv-nav--v6');
    if (!nav) return;
    var pending = false;
    function overflowing() {
      var needed = 0;
      Array.prototype.forEach.call(nav.children, function (child) {
        var style = getComputedStyle(child);
        needed += child.getBoundingClientRect().width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
      });
      return needed > nav.clientWidth - 20;
    }
    /* In steps, each tried only if the one before still does not fit: the
       icons go, then the headings close up. Re-run whenever anything in the
       header changes size — the festive diya appearing on scroll included. */
    function fit() {
      pending = false;
      nav.classList.remove('fv-nav--no-icons', 'fv-nav--compact');
      nav.parentElement.classList.remove('fv-header__bar--no-pip');
      if (getComputedStyle(nav).display === 'none') return;
      if (!overflowing()) return;
      nav.classList.add('fv-nav--no-icons');
      if (!overflowing()) return;
      nav.classList.add('fv-nav--compact');
      if (!overflowing()) return;
      // The narrowest desktops: the header's festive diya steps aside; the
      // festive strip and garland still carry the occasion.
      nav.parentElement.classList.add('fv-header__bar--no-pip');
    }
    function later() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(fit);
    }
    fit();
    if (typeof ResizeObserver === 'function') {
      var bar = nav.parentElement;
      new ResizeObserver(later).observe(bar);
      Array.prototype.forEach.call(bar.children, function (child) {
        if (child !== nav) new ResizeObserver(later).observe(child);
      });
    } else {
      on(window, 'resize', later);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(later);
  }

  function initStickyBuyBar() {
    var bar = $('[data-fv-buybar]');
    // Watch the real purchase row, not a sentinel further down the page.
    var buyRow = $('.fv-buyrow');
    if (!bar || !buyRow || !('IntersectionObserver' in window)) return;

    // The bar belongs on screen only while the original row is above the
    // viewport. Off the bottom — which is the state at first paint — is not the
    // same thing, so it is explicitly excluded.
    // It also steps aside while the footer is on screen, so it never covers
    // the footer's links and details (owner, 2026-10-10).
    var footer = document.querySelector('body > footer, footer');
    var scrolledPast = false;
    var footerInView = false;
    function paint() { bar.classList.toggle('is-visible', scrolledPast && !footerInView); }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === buyRow) {
            scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          } else {
            footerInView = entry.isIntersecting;
          }
        });
        paint();
      },
      { threshold: 0 }
    );
    observer.observe(buyRow);
    if (footer) observer.observe(footer);
  }

  /* The product gallery lives in furniva-product.js, which also keeps the view
     label in step with the image. Binding a second handler here would fight it. */

  /* --------------------------------------------------------------------------
     Real homes — a row of short videos, and the viewer they open into.

     The row itself is an ordinary rail, so the arrows, the end states and the
     trackpad all come from initRails. Nothing here moves it.

     What this owns is the video: tiles play muted and silent only while they
     are on screen, and pressing one opens the viewer with sound.

     Playing every clip in the row at once is the thing that would make this
     expensive, so an IntersectionObserver plays the ones in view and pauses the
     rest — scroll the band off screen and the whole row stops. `preload="none"`
     in the markup means a clip nobody has reached has not been fetched at all.
     -------------------------------------------------------------------------- */
  function initVideoReels() {
    var root = $('[data-fv-vreels]');
    if (!root || root.dataset.fvVreelsReady === 'true') return;
    root.dataset.fvVreelsReady = 'true';

    var tiles = $$('[data-fv-vreel]', root);
    if (!tiles.length) return;

    /* ---- talking to a hosted player --------------------------------------- */
    // YouTube and Vimeo both listen for postMessage commands on their embeds —
    // no SDK, no script tag, no third party in the bundle. That is what lets a
    // linked clip be paused and resumed in place instead of being destroyed and
    // rebuilt, which is what used to make scrolling back reload everything.
    function frameHost(frame) { return frame.src.indexOf('vimeo.com') > -1 ? 'vimeo' : 'youtube'; }

    function frameSend(frame, body) {
      try { frame.contentWindow.postMessage(JSON.stringify(body), '*'); } catch (error) { /* not ready */ }
    }

    function frameCommand(frame, what, muted) {
      if (!frame) return;
      // A command sent before the player is ready is dropped on the floor, so
      // the wanted state is remembered on the element and replayed on load.
      frame.dataset.fvWant = what;
      if (typeof muted === 'boolean') frame.dataset.fvMuted = muted ? '1' : '0';
      var wantMuted = frame.dataset.fvMuted !== '0';
      if (frameHost(frame) === 'vimeo') {
        frameSend(frame, { method: 'setVolume', value: wantMuted ? 0 : 1 });
        frameSend(frame, { method: what === 'play' ? 'play' : 'pause' });
      } else {
        frameSend(frame, { event: 'command', func: wantMuted ? 'mute' : 'unMute', args: [] });
        frameSend(frame, { event: 'command', func: what === 'play' ? 'playVideo' : 'pauseVideo', args: [] });
      }
    }

    // Hand the frame to the browser and reveal it only once it has something to
    // show. Without this the reader watches a white box become a black box
    // become a spinner become a video — four states nobody asked to see.
    function mountFrame(slot, src, muted, title) {
      var frame = document.createElement('iframe');
      frame.className = 'fv-embed';
      frame.src = src;
      frame.dataset.fvMuted = muted ? '1' : '0';
      frame.setAttribute('title', title || '');
      frame.setAttribute('frameborder', '0');
      frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      frame.setAttribute('allowfullscreen', '');
      on(frame, 'load', function () {
        // The load event is the host page, not the first frame of video. A
        // short grace period covers the player's own start-up before the
        // poster underneath is faded away.
        setTimeout(function () {
          slot.classList.add('is-ready');
          if (frame.dataset.fvWant) frameCommand(frame, frame.dataset.fvWant);
        }, 420);
      });
      slot.classList.add('is-loading');
      slot.appendChild(frame);
      return frame;
    }

    /* ---- the row ---------------------------------------------------------- */
    function tileVideo(tile) { return tile.querySelector('video'); }
    function tileSlot(tile) { return tile.querySelector('[data-fv-vtile-embed]'); }

    // Instagram posters wait in data-fv-poster until the tile is reached; a
    // poster attribute in the markup would be downloaded with the page.
    function wakePoster(video) {
      if (video && video.dataset.fvPoster && !video.getAttribute('poster')) video.setAttribute('poster', video.dataset.fvPoster);
    }

    function playTile(tile) {
      var video = tileVideo(tile);
      if (video) {
        wakePoster(video);
        // muted + playsinline is what lets this start without a gesture;
        // a rejected promise is fine and means the browser said no.
        video.play().catch(function () {});
        return;
      }
      var slot = tileSlot(tile);
      if (!slot) return;
      var frame = slot.querySelector('iframe');
      // Mounted once and kept. Scrolling past a tile pauses it; scrolling back
      // resumes the same player, already loaded, rather than starting over.
      if (!frame) frame = mountFrame(slot, slot.dataset.fvVtileEmbed, true, '');
      frameCommand(frame, 'play', true);
    }

    function pauseTile(tile) {
      var video = tileVideo(tile);
      if (video) { video.pause(); return; }
      var slot = tileSlot(tile);
      var frame = slot && slot.querySelector('iframe');
      if (frame) frameCommand(frame, 'pause', true);
    }

    if (typeof IntersectionObserver === 'function' && !reduceMotion.matches) {
      var watcher = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) playTile(entry.target);
          else pauseTile(entry.target);
        });
      }, { rootMargin: '0px 200px', threshold: 0.2 });
      tiles.forEach(function (tile) { watcher.observe(tile); });
    } else {
      // No observer, or motion is unwelcome: leave the posters standing still.
      tiles.forEach(function (tile) {
        var video = tileVideo(tile);
        if (video) { wakePoster(video); video.pause(); }
      });
    }

    // An uploaded clip fades in the same way, on its own first decoded frame.
    tiles.forEach(function (tile) {
      var video = tileVideo(tile);
      if (!video) return;
      var media = tile.querySelector('.fv-vtile__media');
      if (media) media.classList.add('is-loading');
      var ready = function () { if (media) media.classList.add('is-ready'); };
      if (video.readyState >= 2) ready();
      else { on(video, 'loadeddata', ready); on(video, 'playing', ready); }
    });

    // A tab in the background should not be decoding video nobody is watching.
    on(document, 'visibilitychange', function () {
      if (document.hidden) tiles.forEach(pauseTile);
    });

    /* ---- the viewer ------------------------------------------------------- */
    var viewer = $('[data-fv-vview]', root);
    var stage = $('[data-fv-vview-stage]', root);
    var prev = $('[data-fv-vview-prev]', root);
    var next = $('[data-fv-vview-next]', root);
    var sound = $('[data-fv-vview-sound]', root);
    if (!viewer || !stage || !viewer.showModal) return;

    var index = 0;
    // The row is silent; opening a video is a deliberate press, so it starts
    // with sound. If the browser refuses to autoplay audible media anyway we
    // drop back to muted rather than showing a frozen frame.
    var muted = false;
    var opener = null;
    // One panel per clip the reader has actually opened, kept in the stage and
    // hidden rather than thrown away. Removing an <iframe> from the document
    // and putting it back reloads it — browsers give no way around that — so
    // the only way to reopen a video where it left off is never to unmount it.
    var panels = {};
    var order = [];
    var KEEP = 5;

    function panelMedia(panel) { return panel ? panel.querySelector('video, iframe') : null; }

    function stopPanel(panel) {
      var media = panelMedia(panel);
      if (!media) return;
      if (media.tagName === 'VIDEO') media.pause();
      else frameCommand(media, 'pause');
    }

    function markSound() {
      if (!sound) return;
      sound.setAttribute('aria-pressed', muted ? 'true' : 'false');
      sound.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      sound.classList.toggle('is-on', !muted);
    }

    function buildPanel(i) {
      var detail = tiles[i].querySelector('[data-fv-vreel-detail]');
      var panel = document.createElement('div');
      panel.className = 'fv-vview__panel';
      panel.dataset.fvPanel = String(i);
      if (detail) panel.appendChild(detail.content.cloneNode(true));

      // The template carries the embed address, not a live frame, so the panel
      // decides when to mount it — and mounts it exactly once.
      var slot = panel.querySelector('[data-fv-vview-embed]');
      if (slot) mountFrame(slot, slot.dataset.fvVviewEmbed, muted, tiles[i].dataset.fvShareTitle || '');

      var video = panel.querySelector('video');
      if (video) {
        var media = panel.querySelector('.fv-vview__media');
        if (media) {
          media.classList.add('is-loading');
          var ready = function () { media.classList.add('is-ready'); };
          if (video.readyState >= 2) ready();
          else { on(video, 'loadeddata', ready); on(video, 'playing', ready); }
        }
      }

      stage.appendChild(panel);
      panels[i] = panel;
      order.push(i);

      // A reader who works through a long row should not end up with a dozen
      // live players behind the one they are watching.
      while (order.length > KEEP) {
        var stale = order.shift();
        if (stale === i || !panels[stale]) continue;
        stopPanel(panels[stale]);
        panels[stale].remove();
        delete panels[stale];
      }
      return panel;
    }

    function startPanel(panel) {
      var video = panel.querySelector('video');
      if (video) {
        video.muted = muted;
        video.controls = false;
        video.play().catch(function () {
          if (muted) return;
          muted = true;
          video.muted = true;
          markSound();
          video.play().catch(function () {});
        });
        return;
      }
      var frame = panel.querySelector('iframe');
      if (frame) frameCommand(frame, 'play', muted);
    }

    function paint(i) {
      index = (i + tiles.length) % tiles.length;
      Object.keys(panels).forEach(function (key) {
        if (Number(key) === index) return;
        panels[key].hidden = true;
        stopPanel(panels[key]);
      });

      var panel = panels[index] || buildPanel(index);
      panel.hidden = false;
      // Reopening a clip should carry on, not start the same ten seconds again.
      startPanel(panel);

      markSound();
      var many = tiles.length > 1;
      if (prev) prev.hidden = !many;
      if (next) next.hidden = !many;
    }

    function open(tile) {
      opener = tile;
      // The row keeps playing behind a modal it cannot be seen through, which
      // is decoding nobody benefits from.
      tiles.forEach(pauseTile);
      paint(tiles.indexOf(tile));
      viewer.showModal();
    }

    function close() { viewer.close(); }

    tiles.forEach(function (tile) {
      on(tile, 'click', function () { open(tile); });
    });

    on(prev, 'click', function () { paint(index - 1); });
    on(next, 'click', function () { paint(index + 1); });
    on($('[data-fv-vview-close]', root), 'click', close);

    on(sound, 'click', function () {
      muted = !muted;
      var panel = panels[index];
      var video = panel && panel.querySelector('video');
      if (video) {
        video.muted = muted;
        video.play().catch(function () {});
      } else {
        // A hosted player is muted by command, not by reloading it with a
        // different query string — which is what used to restart the clip.
        var frame = panel && panel.querySelector('iframe');
        if (frame) frameCommand(frame, 'play', muted);
      }
      markSound();
    });

    /* ---- share ------------------------------------------------------------ */
    var share = $('[data-fv-vview-share]', root);
    on(share, 'click', function () {
      var tile = tiles[index];
      var data = {
        title: tile.dataset.fvShareTitle || document.title,
        url: tile.dataset.fvShareUrl || window.location.href
      };

      // navigator.share is the native sheet — Messages, WhatsApp, whatever the
      // reader has. It needs a secure context and a real gesture, both of which
      // we have here. Where it does not exist, the link goes to the clipboard
      // and the button says so for a moment.
      if (navigator.share) {
        navigator.share(data).catch(function () {});
        return;
      }
      var done = function () {
        share.classList.add('is-copied');
        share.setAttribute('aria-label', 'Link copied');
        setTimeout(function () {
          share.classList.remove('is-copied');
          share.setAttribute('aria-label', 'Share this video');
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(data.url).then(done, function () {});
      } else {
        window.prompt('Copy this link', data.url);
      }
    });

    on(viewer, 'click', function (event) {
      // The backdrop is the dialog itself; a press on the stage must not close.
      if (event.target === viewer) close();
    });
    on(viewer, 'cancel', function (event) { event.preventDefault(); close(); });
    on(viewer, 'keydown', function (event) {
      if (tiles.length < 2) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); paint(index - 1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); paint(index + 1); }
    });
    on(viewer, 'close', function () {
      // The panels stay where they are, paused and hidden, so that opening the
      // same clip again resumes it instead of loading it from scratch.
      Object.keys(panels).forEach(function (key) {
        panels[key].hidden = true;
        stopPanel(panels[key]);
      });
      if (opener && opener.isConnected) opener.focus();
      opener = null;
      // The row picks its own playback back up through the observer.
      if (!reduceMotion.matches) tiles.forEach(playTile);
    });

    /* ---- add to cart, from the card under the video ----------------------- */
    on(root, 'click', function (event) {
      var button = event.target.closest('[data-fv-reel-add]');
      if (!button) return;
      event.preventDefault();

      var panel = panels[index];
      var state = panel ? panel.querySelector('[data-fv-reel-state]') : null;
      if (state) { state.textContent = ''; state.className = 'fv-reel__state'; }
      if (window.FurnivaAddFx) window.FurnivaAddFx.working(button);

      var routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variant), quantity: 1 }] })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (error) {
              throw new Error(error.description || error.message || 'Could not add this piece.');
            });
          }
          return response.json();
        })
        .then(function (cart) {
          if (window.FurnivaAddFx) window.FurnivaAddFx.added(button, cart);
          else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function (error) {
          if (window.FurnivaAddFx) window.FurnivaAddFx.failed(button);
          if (state) {
            state.textContent = error.message;
            state.className = 'fv-reel__state is-error';
          }
        });
    });
  }

  function initAboutHero() {
    var hero = $('[data-fv-abouthero]');
    if (!hero) return;
    var frames = $$('[data-fv-abouthero-frame]', hero);
    var dots = $$('[data-fv-abouthero-dot]', hero);
    if (frames.length < 2) return;

    var at = 0;
    var timer = null;
    var wait = Number(hero.dataset.interval) || 6000;

    function show(i) {
      at = (i + frames.length) % frames.length;
      frames.forEach(function (frame, n) { frame.classList.toggle('is-on', n === at); });
      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-on', n === at);
        dot.setAttribute('aria-selected', n === at ? 'true' : 'false');
      });
    }

    function start() {
      stop();
      // A masthead that keeps moving while someone is reading the paragraph
      // beside it is a distraction, so it stops once it is off screen and
      // whenever the tab is not being looked at.
      if (reduceMotion.matches) return;
      timer = setInterval(function () { show(at + 1); }, wait);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    dots.forEach(function (dot, n) {
      on(dot, 'click', function () { show(n); start(); });
    });

    on(hero, 'mouseenter', stop);
    on(hero, 'mouseleave', start);
    on(document, 'visibilitychange', function () { if (document.hidden) stop(); else start(); });

    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { if (entry.isIntersecting) start(); else stop(); });
      }, { threshold: 0.12 }).observe(hero);
    } else {
      start();
    }
  }

  function initCountUp() {
    var numbers = $$('[data-fv-count]');
    if (!numbers.length) return;

    // The finished figure is already in the markup, so anything that does not
    // get this far — no observer, reduced motion, no script at all — shows the
    // real number rather than a zero. The animation only ever replaces a
    // number that is already correct.
    if (reduceMotion.matches || typeof IntersectionObserver !== 'function') return;

    var format = function (n) { return n.toLocaleString('en-IN'); };

    var run = function (el) {
      var to = Number(el.dataset.to);
      if (!isFinite(to) || to <= 0) return;
      var span = 1400;
      var started = null;

      var step = function (now) {
        if (started === null) started = now;
        var through = Math.min((now - started) / span, 1);
        // Ease out: the number slows into its final value instead of stopping
        // dead, which is what makes it read as counting rather than flickering.
        var eased = 1 - Math.pow(1 - through, 3);
        el.textContent = format(Math.round(to * eased));
        if (through < 1) requestAnimationFrame(step);
        else el.textContent = format(to);
      };

      el.textContent = format(0);
      requestAnimationFrame(step);
    };

    var watcher = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // Once only. A figure that re-counts every time it scrolls past turns
        // a statement of fact into a fidget.
        watcher.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.6 });

    numbers.forEach(function (el) { watcher.observe(el); });
  }

  function initBestSellers() {
    var root = $('[data-fv-tops]');
    if (!root) return;
    var links = $$('[data-fv-tops-link]', root);
    var panels = $$('[data-fv-tops-panel]', root);
    if (links.length < 2 || !panels.length) return;

    // Until this runs every panel is on the page, one under another, and the
    // links are ordinary anchors to them. Saying so on the root lets the CSS
    // drop the spacing that only the stacked version needs.
    root.setAttribute('data-fv-tops-ready', 'true');

    function show(id, push) {
      var found = false;
      panels.forEach(function (panel) {
        var match = panel.dataset.fvTopsPanel === id;
        panel.hidden = !match;
        if (match) found = true;
      });
      if (!found) return false;

      links.forEach(function (link) {
        var match = link.dataset.fvTopsLink === id;
        link.classList.toggle('is-active', match);
        if (match) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      // The address bar keeps up so a category can be linked to and comes back
      // on reload, but without adding a history entry per press unless the
      // reader actually chose one.
      if (window.history && window.history.replaceState) {
        var url = window.location.pathname + window.location.search + '#' + id;
        if (push && window.history.pushState) window.history.pushState(null, '', url);
        else window.history.replaceState(null, '', url);
      }
      return true;
    }

    links.forEach(function (link) {
      on(link, 'click', function (event) {
        event.preventDefault();
        show(link.dataset.fvTopsLink, true);
        // The list is sticky; scrolling the panel to the top of it would fight
        // that. Switching in place is what the reader expects from a tab.
      });
    });

    on(window, 'popstate', function () {
      var id = window.location.hash.replace('#', '');
      if (!id || !show(id, false)) show(panels[0].dataset.fvTopsPanel, false);
    });

    // A deep link decides the opening panel; otherwise it is the first.
    var initial = window.location.hash.replace('#', '');
    if (!initial || !show(initial, false)) show(panels[0].dataset.fvTopsPanel, false);
  }

  function initWhyQuestions() {
    var root = $('[data-fv-why]');
    if (!root || root.dataset.fvWhyReady === 'true') return;
    var steps = $$('[data-fv-why-step]', root);
    if (steps.length < 2) return;
    root.dataset.fvWhyReady = 'true';
    root.classList.add('is-enhanced');
    var prev = $('[data-fv-why-prev]', root);
    var next = $('[data-fv-why-next]', root);
    var count = $('[data-fv-why-count]', root);
    var progress = $('[data-fv-why-progress]', root);
    var index = 0;
    var moving = false;

    function show(target) {
      if (moving || target < 0 || target >= steps.length || target === index) return;
      moving = true;
      root.classList.toggle('is-reversing', target < index);
      var current = steps[index];
      if (!reduceMotion.matches) current.classList.add('is-leaving');
      window.setTimeout(function () {
        current.classList.remove('is-leaving');
        current.hidden = true;
        index = target;
        var active = steps[index];
        root.dataset.tone = active.dataset.tone || 'clay';
        active.hidden = false;
        active.classList.remove('is-entering');
        if (!reduceMotion.matches) {
          void active.offsetWidth;
          active.classList.add('is-entering');
        }
        if (count) count.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(steps.length).padStart(2, '0');
        if (progress) progress.style.width = ((index + 1) / steps.length * 100) + '%';
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === steps.length - 1;
        moving = false;
      }, reduceMotion.matches ? 0 : 160);
    }

    /* ---- one height for every question ---------------------------------
       The panel used to be as tall as whichever answer was showing, so moving
       through the questions made it grow and shrink and dragged the arrows and
       the progress bar up and down with it — unpleasant on a phone, where the
       difference between the shortest and longest answer is most of a screen.

       The slide list is given a floor equal to the tallest slide, so the panel
       settles once and never moves again. The measurement has to un-hide each
       slide to read it, because `[hidden]` is display:none here by design; that
       happens inside a single synchronous pass, so the browser never paints an
       intermediate state and nothing flickers.

       A floor rather than a fixed height: .fv-why__slides is a flex child, so
       if the panel's own min-height is the larger of the two it still stretches
       to fill it rather than leaving a gap under the copy.
       -------------------------------------------------------------------- */
    var slidesBox = $('.fv-why__slides', root);

    function lockHeight() {
      if (!slidesBox) return;
      slidesBox.style.minHeight = '';
      var tallest = 0;
      steps.forEach(function (step) {
        var wasHidden = step.hidden;
        step.hidden = false;
        // The fractional height, rounded up once at the end: offsetHeight
        // rounds per slide, which left the floor a pixel short of the tallest.
        tallest = Math.max(tallest, step.getBoundingClientRect().height);
        step.hidden = wasHidden;
      });
      if (tallest <= 0) return;
      // box-sizing is border-box throughout this theme, so a min-height of the
      // tallest slide alone would have the list's own padding eaten out of it —
      // which is exactly what let the three longest answers push past the floor.
      var box = getComputedStyle(slidesBox);
      var chrome =
        parseFloat(box.paddingTop) + parseFloat(box.paddingBottom) +
        parseFloat(box.borderTopWidth) + parseFloat(box.borderBottomWidth);
      slidesBox.style.minHeight = Math.ceil(tallest + (chrome || 0)) + 'px';
    }

    lockHeight();
    // Web fonts land after first paint and change every line's height with them.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(lockHeight).catch(function () {});
    }
    // Width changes rewrap the answers, so the tallest one can change too.
    if (typeof ResizeObserver === 'function') {
      var lastWidth = root.getBoundingClientRect().width;
      new ResizeObserver(function () {
        var width = root.getBoundingClientRect().width;
        if (Math.abs(width - lastWidth) < 1) return;
        lastWidth = width;
        lockHeight();
      }).observe(root);
    }

    if (progress) progress.style.width = (100 / steps.length) + '%';
    on(prev, 'click', function () { show(index - 1); });
    on(next, 'click', function () { show(index + 1); });

    // Links and buttons are left alone; the arrows remain the keyboard and
    // reduced-motion path. The gesture used to be gated on the phone
    // breakpoint, which meant the panel could not be swiped on a tablet or a
    // touch laptop even though the finger was right there.
    onSwipe($('.fv-why__panel', root), function (direction) {
      show(index + direction);
    }, 'a, button');

    // The same movement with a mouse or a trackpad. The panel is a stack of
    // slides rather than a scroller, so the gesture is read as one step.
    stepGestures($('.fv-why__panel', root), function (direction) {
      show(index + direction);
    });
  }

  function initIdeas() {
    var grid = $('[data-fv-ideas-grid]');
    var pills = $$('[data-fv-topic]');
    if (!grid || !pills.length) return;

    var cards = $$('[data-fv-guide]', grid);
    var empty = $('[data-fv-topic-empty]');

    function apply(topic) {
      var shown = 0;
      cards.forEach(function (card) {
        var match = !topic || card.dataset.topic === topic;
        card.hidden = !match;
        if (match) shown++;
      });
      pills.forEach(function (pill) {
        var on = (pill.dataset.fvTopic || '') === (topic || '');
        pill.classList.toggle('is-on', on);
        pill.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (empty) empty.hidden = shown !== 0;
      grid.hidden = shown === 0;
    }

    pills.forEach(function (pill) {
      on(pill, 'click', function () { apply(pill.dataset.fvTopic || ''); });
    });
    on($('[data-fv-topic-clear]'), 'click', function () { apply(''); });
  }

  /* -------------------------------------------------------------- marquee -- */
  // The sticky header group is offset by the marquee's height so the marquee
  // scrolls away and the header lands flush against the top of the viewport.
  // Measured rather than hard-coded: the strip re-wraps at narrow widths.
  function initMarqueeHeight() {
    var group = document.getElementById('header-group');
    var marquee = document.querySelector('.fv-mq');
    if (!group || !marquee) return;

    function measure() {
      var h = Math.round(marquee.getBoundingClientRect().height);
      if (h > 0) group.style.setProperty('--fv-marquee-h', h + 'px');
    }
    measure();
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(measure).observe(marquee);
    } else {
      window.addEventListener('resize', measure);
    }
  }

  /* Product-card photography is a horizontal rail on every viewport. Dots
     follow the nearest settled image; navigation remains a product link. */
  function initCardMedia(scope) {
    $$('[data-fv-card-media]', scope || document).forEach(function (rail) {
      if (rail.dataset.fvCardMediaBound) return;
      rail.dataset.fvCardMediaBound = '1';
      var dots = $$('[data-fv-card-dot]', rail.parentElement);
      if (!dots.length) return;
      var paint = function () {
        var width = rail.clientWidth || 1;
        var index = Math.max(0, Math.min(dots.length - 1, Math.round(rail.scrollLeft / width)));
        dots.forEach(function (dot, i) {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      on(rail, 'scroll', paint, { passive: true });

      /* Nothing is bound here. Above 760px this rail is not rendered at all —
         the card shows its pair of stills and turns over on hover — and below
         it, a finger on a native scroller is the whole interaction. */
      paint();
    });
  }

  var photoObserver = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('is-photo-loading', entry.isIntersecting && entry.target.__fvPhotoPending);
        });
      }, { rootMargin: '120px 0px' })
    : null;

  function photoInView(media) {
    var rect = media.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight + 120 && rect.right > 0 && rect.left < window.innerWidth + 120;
  }

  function initPhotoLoading(scope) {
    /* The other photo frames on the page — guide cards and room tiles — get the
       same treatment, but they hold one plain image rather than the product
       card's desktop/hover/mobile set, so they are handled on their own. */
    $$('.fv-guide__media, .fv-roomtile__media', scope || document).forEach(function (frame) {
      if (frame.dataset.fvPhotoBound) return;
      frame.dataset.fvPhotoBound = '1';
      var image = frame.querySelector('img');
      if (!image || image.complete) return;
      frame.classList.add('is-photo-loading');
      var settle = function () { frame.classList.remove('is-photo-loading'); };
      image.addEventListener('load', settle, { once: true });
      image.addEventListener('error', settle, { once: true });
      /* A ceiling. These sit in rails, and a lazy image scrolled off the right
         of one has not started loading at all — so neither event is coming, and
         without this the bar spins over a card nobody has reached yet. Ten
         seconds is longer than any photograph that is actually arriving. */
      window.setTimeout(settle, 10000);
    });

    $$('.fv-card > .fv-media', scope || document).forEach(function (media) {
      if (media.dataset.fvPhotoBound) return;
      media.dataset.fvPhotoBound = '1';
      var images = $$('.fv-media__desktop img:not(.fv-media__hover), .fv-media__mobile-slide img', media);
      if (!images.length) return;

      /* Whether this card is still waiting is asked of the card, not tracked
         against one image chosen up front.

         Two things move under that older arrangement and broke it. The card now
         renders one of two sets depending on the pointer, so a designated image
         can be the hidden one — and a lazy image in a display:none subtree never
         loads, so its `load` never fires. And a card inside a closed explorer
         panel has no rendered images at all when it is first bound, so there is
         nothing to designate yet; it only acquires one when its group opens.

         So: the pending state is recomputed from whatever is on screen at the
         time, and recomputed again whenever that changes. */
      function shown() {
        return images.filter(function (image) {
          return image.getClientRects().length > 0;
        });
      }

      function refresh() {
        var visible = shown();
        // Nothing rendered — the card is in a panel that is closed. It is not
        // loading, it is not anything; say nothing until it is on screen.
        if (!visible.length) {
          media.__fvPhotoPending = false;
          media.classList.remove('is-photo-loading');
          return;
        }
        /* The image being presented, not all of them.

           On a phone the card is a swipe rail and every slide has layout boxes,
           so asking "is any of these still loading" made a card claim to be
           loading because slide five had not arrived — and slide five is lazy
           and sitting off the right-hand end of a rail nobody has swiped, so it
           may never arrive at all. The customer is looking at the first one;
           that is the one the state is about. */
        var pending = !visible[0].complete;
        media.__fvPhotoPending = pending;
        if (pending) {
          if (photoObserver) photoObserver.observe(media);
          if (photoInView(media)) media.classList.add('is-photo-loading');
        } else {
          media.classList.remove('is-photo-loading');
          if (photoObserver) photoObserver.unobserve(media);
          // A photo that failed once and then arrived (a cold CDN resize, a
          // srcset switch) is no longer unavailable — the label used to stay
          // printed over a photo that was plainly there.
          media.classList.toggle('fv-media--error', !visible[0].naturalWidth);
        }
      }
      // Kept on the element so the explorer can ask for a recount the moment a
      // group is revealed, without this function being exported anywhere.
      media.__fvPhotoRefresh = refresh;

      images.forEach(function (image) {
        var wasPending = !image.complete;
        var settle = function () {
          refresh();
          // Cached photos are shown immediately. Only a genuinely loading,
          // on-screen photo gets a reveal; off-screen rails do no animation work.
          if (wasPending && image.naturalWidth && photoInView(media) && !reduceMotion.matches) {
            image.classList.add('is-photo-ready');
          }
        };
        if (image.complete) settle();
        else {
          image.addEventListener('load', settle, { once: true });
          image.addEventListener('error', settle, { once: true });
        }
      });

      refresh();
    });

    /* A card revealed by opening an explorer panel has to be recounted: it had
       no rendered images when it was bound, and nothing about the images
       themselves changes when the panel opens, so no load event is coming. */
    $$('.fv-explorer', scope || document).forEach(function (explorer) {
      if (explorer.dataset.fvPhotoWatch) return;
      explorer.dataset.fvPhotoWatch = '1';
      if (typeof MutationObserver !== 'function') return;
      new MutationObserver(function () {
        $$('.fv-card > .fv-media', explorer).forEach(function (media) {
          if (media.__fvPhotoRefresh) media.__fvPhotoRefresh();
        });
      }).observe(explorer, { attributes: true, attributeFilter: ['hidden'], subtree: true });
    });
  }

  function initAccountGreeting() {
    var heading = $('[data-fv-account-greeting]');
    if (!heading) return;
    var hour = new Date().getHours();
    var greeting = 'Hello';
    if (hour < 5) greeting = 'Hello, night owl';
    else if (hour < 12) greeting = 'Good morning';
    else if (hour < 17) greeting = 'Good afternoon';
    else if (hour < 22) greeting = 'Good evening';
    else greeting = 'Hello, night owl';
    var name = (heading.dataset.name || '').trim();
    heading.textContent = greeting + (name ? ', ' + name : '');
  }

  function initPageProgress() {
    var bar = $('[data-fv-page-progress]');
    if (!bar || bar.dataset.fvProgressBound) return;
    bar.dataset.fvProgressBound = '1';
    /* The bar starts only for a click that will actually navigate.

       It used to decide on `document`, at the moment of the click. Other
       handlers on `document` run after it — the cart's Save for later and
       Remove are real links that furniva-cart.js intercepts and turns into an
       AJAX change — so it saw a link, started the bar, and the page never
       left. The bar sat there half-loaded over a cart that had updated fine.

       Listening on `window` runs after every document handler has had its
       say, so `defaultPrevented` is settled by then. And a safety release,
       because anything that swallows a navigation some other way (an app, a
       dialog) must not leave the bar up for good. */
    var release = null;
    on(window, 'click', function (event) {
      var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      var url;
      try { url = new URL(link.href, window.location.href); } catch (error) { return; }
      if (url.origin !== window.location.origin || url.href === window.location.href || url.hash && url.pathname === window.location.pathname) return;
      bar.classList.add('is-loading');
      clearTimeout(release);
      release = setTimeout(function () { bar.classList.remove('is-loading'); }, 8000);
    });
    on(window, 'pageshow', function () {
      clearTimeout(release);
      bar.classList.remove('is-loading');
    });
  }

  function init() {
    initMega();
    initNavDrawer();
    initSearch();
    initCart();
    initCardQuickAdd();
    initSearchGreeting();
    watchSearchGreeting();
    /* Saving a name in the profile changes who the greeting is addressing, and
       the panel never reloads the page. furniva-account.js raises this whenever
       the stored name changes, so search is repainted with the rest. */
    on(document, 'furniva:account-name', function () {
      initSearchGreeting();
    });
    initRowGestures();
    initExplorerRails();
    initCarousels();
    initRails();
    window.furnivaInitRails = initRails;
    initNavFit();
    initBottomNavHeight();
    initStickyBuyBar();
    initMarqueeHeight();
    initVideoReels();
    initBestSellers();
    initAboutHero();
    initCountUp();
    initWhyQuestions();
    initIdeas();
    initCardMedia();
    initPhotoLoading();
    initAccountGreeting();
    initPageProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place; re-bind what it replaced.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('furniva:collection:updated', function () { initCardMedia(); });
  document.addEventListener('furniva:collection:updated', function () { initPhotoLoading(); });
  document.addEventListener('furniva:collection:updated', function () { initCardQuickAdd(); });
})();

/* ============================================================================
   Motion helpers shared by the cart, the saved list and search (owner,
   2026-10-10: "a premium website, smooth animations").

   FurnivaMotion.leave(el)  the piece glides out and the list closes up over
                            it; resolves when done. Instant for reduced motion.
   FurnivaToast.show(text, link?)  a short note slides up from the foot of the
                            screen ("Saved for later · View saved") and away.
   Soft photos              photos on the cart, the saved list and the search
                            panel shimmer while they load and fade in, including
                            ones put on the page later. Product tiles have
                            their own (initPhotoLoading).
   ============================================================================ */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function leave(el) {
    if (!el || reduce.matches || !el.animate) return Promise.resolve();
    var h = el.getBoundingClientRect().height;
    var cs = getComputedStyle(el);
    el.style.overflow = 'hidden';
    el.style.pointerEvents = 'none';
    var fade = el.animate(
      [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(-18px)' }],
      { duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
    );
    return fade.finished.then(function () {
      var close = el.animate(
        [
          { height: h + 'px', marginTop: cs.marginTop, marginBottom: cs.marginBottom, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom },
          { height: '0px', marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px' }
        ],
        { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
      );
      return close.finished;
    }).catch(function () {});
  }
  window.FurnivaMotion = { leave: leave };

  var toastEl = null;
  var toastTimer = null;
  function show(text, link) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'fv-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var span = document.createElement('span');
    span.textContent = text;
    toastEl.appendChild(span);
    if (link && link.href) {
      var a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.label || 'View';
      toastEl.appendChild(a);
    }
    toastEl.classList.remove('is-shown');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-shown');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-shown'); }, 2800);
  }
  window.FurnivaToast = { show: show };

  var SOFT = '.fv-cartitem img, [data-fv-wishlist-grid] img, [data-fv-search-results] img, .fv-cartdrawer img';
  function soften(img) {
    if (img.dataset.fvSoft || img.closest('.fv-card > .fv-media')) return;
    img.dataset.fvSoft = '1';
    if (img.complete && img.naturalWidth) return;
    var holder = img.parentElement;
    img.classList.add('fv-soft');
    if (holder) holder.classList.add('fv-soft-wait');
    var done = function () {
      img.classList.add('is-in');
      if (holder) holder.classList.remove('fv-soft-wait');
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  }
  function scan(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(SOFT), soften);
  }
  function start() {
    scan(document);
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'IMG' && n.matches(SOFT)) soften(n);
          else if (n.querySelectorAll) scan(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
